import { xchacha20poly1305 } from '@noble/ciphers/chacha';
import { utf8ToBytes, bytesToHex, hexToBytes } from '@noble/ciphers/utils';

const NONCE_LENGTH = 24; // 192-bit nonce for XChaCha20

// Generate random bytes using browser's crypto API
function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Encrypt plaintext string using XChaCha20-Poly1305.
 * Returns hex-encoded string: nonce || ciphertext || auth_tag
 */
export function encrypt(key: Uint8Array, plaintext: string): string {
  const nonce = randomBytes(NONCE_LENGTH);
  const data = utf8ToBytes(plaintext);

  const cipher = xchacha20poly1305(key, nonce);
  const ciphertext = cipher.encrypt(data);

  // Combine nonce + ciphertext for storage
  const combined = new Uint8Array(nonce.length + ciphertext.length);
  combined.set(nonce);
  combined.set(ciphertext, nonce.length);

  return bytesToHex(combined);
}

/**
 * Decrypt hex-encoded ciphertext using XChaCha20-Poly1305.
 * Expects format: nonce || ciphertext || auth_tag
 */
export function decrypt(key: Uint8Array, encryptedHex: string): string {
  const combined = hexToBytes(encryptedHex);

  const nonce = combined.slice(0, NONCE_LENGTH);
  const ciphertext = combined.slice(NONCE_LENGTH);

  const cipher = xchacha20poly1305(key, nonce);
  const plaintext = cipher.decrypt(ciphertext);

  return new TextDecoder().decode(plaintext);
}

/**
 * Encrypt an object to JSON, then encrypt the JSON string.
 */
export function encryptObject<T extends object>(key: Uint8Array, obj: T): string {
  const json = JSON.stringify(obj);
  return encrypt(key, json);
}

/**
 * Decrypt a hex string and parse as JSON.
 */
export function decryptObject<T>(key: Uint8Array, encryptedHex: string): T {
  const json = decrypt(key, encryptedHex);
  return JSON.parse(json) as T;
}

/**
 * Verification blob constant - used to verify correct passphrase.
 */
export const VERIFICATION_PLAINTEXT = 'mysecureroom-key-verification-v1';

/**
 * Create verification blob for a new key.
 */
export function createVerificationBlob(key: Uint8Array): string {
  return encrypt(key, VERIFICATION_PLAINTEXT);
}

/**
 * Verify that a key is correct by decrypting the verification blob.
 */
export function verifyKey(key: Uint8Array, verificationBlob: string): boolean {
  try {
    const decrypted = decrypt(key, verificationBlob);
    return decrypted === VERIFICATION_PLAINTEXT;
  } catch {
    return false;
  }
}
